import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useConfirmation } from 'app/components/ConfirmationContext'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { gqlMutation } from 'app/lib/graphqlClient'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { useWorkspaceInviteStore, useRoleStore } from 'app/stores/StoreContext'
import { logError, logWarn } from 'lib/logging'
import { useWorkspace } from 'app/utils'
import type { RoleJson } from 'app/types/entities'
import { graphql } from 'app/generated/gql'

// `deleteStaff` returns a Boolean. The old raw string selected `{ id }` on it, which
// the API rejects, so deleting an invited staff member never worked from here.
const DELETE_STAFF_MUTATION = graphql(`
  mutation DeleteStaff($id: ID!) {
    deleteStaff(id: $id)
  }
`)

export type InviteRow = {
  id: string
  email: string
  roleName: string
  staffId?: string
  staffName?: string
  createdAt: string
}

export type UseInvitedUsersReturn = {
  invites: InviteRow[]
  canManageUsers: boolean
  handleDelete: (invite: InviteRow) => Promise<void>
}

export function useInvitedUsers(): UseInvitedUsersReturn {
  const store = useWorkspaceInviteStore()
  const roleStore = useRoleStore()
  const currentWorkspace = useWorkspace()
  const workspaceId = currentWorkspace?.id
  const checkPermission = useCheckPermission()
  const canManageUsers = checkPermission([`org:settings:manage-users`, `users:manage`])
  const [confirm] = useConfirmation()
  const pendingStaffRef = useRef<{ staffId: string; staffName: string } | null>(null)
  const [roles, setRoles] = useState<RoleJson[]>([])
  const [hits, setHits] = useState<typeof store.hits>([])

  useEffect(() => {
    store
      .fetchList()
      .then(() => {
        setHits([...store.hits])
      })
      .catch((err) => logError(`Error fetching invites`, err))
  }, [store])

  useEffect(() => {
    if (!workspaceId) return
    roleStore.fetchList({ pagination: { page: 0, limit: 1000 } }).then(() => {
      setRoles((roleStore.list?.hits ?? []) as RoleJson[])
    })
  }, [workspaceId])

  const invites: InviteRow[] = useMemo(
    () =>
      hits.map((inv) => {
        const role = roles.find((r) => r.id === inv.roleId)
        return {
          id: inv.id,
          email: (inv.email as string) ?? ``,
          roleName: role?.name ?? ``,
          staffId: inv.staffId as string | undefined,
          staffName: undefined,
          createdAt: (inv.createdAt as string) ?? ``,
        }
      }),
    [hits, roles],
  )

  const deleteStaff = async (staffId: string): Promise<{ success?: boolean; error?: string }> => {
    const result = await gqlMutation(DELETE_STAFF_MUTATION, { id: staffId })
    if (result.error) return { error: result.error.message }
    return { success: true }
  }

  const handleDelete = async (invite: InviteRow) => {
    try {
      await confirm({
        title: `Delete Invitation`,
        description: `Are you sure you want to delete the invitation for ${invite.email}?`,
        variant: `danger`,
      })
      if (invite.staffId && invite.staffName) {
        pendingStaffRef.current = { staffId: invite.staffId, staffName: invite.staffName }
      }

      const result = await store.remove(invite.id)
      if (!result.error) {
        setHits([...store.hits])
        notifySuccess(`Invitation deleted`)

        const pending = pendingStaffRef.current
        if (invite.staffId && pending && pending.staffId === invite.staffId) {
          pendingStaffRef.current = null
          try {
            await confirm({
              title: `Delete Staff`,
              description: `The invitation had a linked staff member "${pending.staffName}". Would you like to delete this staff record as well?`,
              variant: `danger`,
              submitLabel: `Delete Staff`,
            })
            const staffResult = await deleteStaff(pending.staffId)
            if (staffResult.error) {
              notifyError(staffResult.error)
            } else {
              notifySuccess(`Staff deleted`)
            }
          } catch (err) {
            logWarn(`User cancelled staff deletion`, err)
          }
        }
      }
    } catch {
      logWarn(`User cancelled invite deletion`)
    }
  }

  return { invites, canManageUsers, handleDelete }
}
